import { countByAsRecord, nonNullable } from 'lib/array/arrayUtils'
import loadScript from 'lib/window/loadScript'
import config from 'constants/config'
import { getCurrencySymbolAffixes } from 'lib/format/formatCurrencyIntl'

export const ARENA_SCRIPT_SRC = 'https://static.ticketswitch.com/arena/v1.0.2/main.js'

type ArenaPositionValue = 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'none'
type ArenaBorderValue = 'none' | 'all' | 'top' | 'bottom' | 'left' | 'right' | 'top-bottom' | 'left-right'

interface ArenaPosition {
  position: ArenaPositionValue
  border?: ArenaBorderValue
}

export type ArenaStatus = 'idle' | 'creating-session' | 'loading-script' | 'creating-arena' | 'ready' | 'reserving' | 'reserved' | 'failed'
export type ArenaErrorType =
  | 'arena-api-unavailable'
  | 'arena-create-failed'
  | 'event-failed'
  | 'missing-trolley-token'
  | 'reserve-failed'
  | 'script-load-failed'
  | 'selection-session-failed'

export type ArenaReservationFailureReason =
  | 'leaves-single-seat'
  | 'missing-transaction'
  | 'reserve-failed'
  | 'seat-unavailable'
  | 'some-seats-unavailable'

export interface ArenaConfigOptions {
  controls?: ArenaPosition
  disableScrollToZoom?: boolean
  hasCustomLegend?: boolean
  hideBanner?: boolean
  hideCurrencySymbol?: boolean
  hideOfferMessages?: boolean
  legend?: ArenaPosition
  legendColours?: Array<ArenaLegendColour>
  mapPaddingBottom?: number
  mapPaddingRight?: number
  minimap?: ArenaPosition
  showBasket?: boolean
  unhandledSeats?: ArenaPosition
  zoomDepthOnSeatClick?: number
}

export interface ArenaCreateOptions {
  apiAuthToken: string
  apiBase: string
  eventId: string
  perfId: string
  tyrBase: string
  options: ArenaConfigOptions
}

export interface ArenaReserveResult {
  input_contained_unavailable_order?: boolean
  minutes_left_on_reserve?: number
  reserve_iso8601_date_and_time?: string
  transaction_status?: string
  trolleyToken?: string
  trolley_token?: string
  trolley_contents?: {
    transaction_uuid?: string
  }
  unreserved_orders?: Array<ArenaUnreservedOrder>
}

export interface ArenaUnreservedOrder {
  reserve_failure_comment?: string
  seat_request_status?: string
}

export interface ArenaApp {
  on: (eventName: ArenaEventName, listener: (payload: ArenaEventPayload) => void) => void
  off?: (eventName: ArenaEventName, listener: (payload: ArenaEventPayload) => void) => void
  destroy?: () => void
  reserveSeats?: () => Promise<ArenaReserveResult | string | undefined>
  setCustomLegend?: (legend: ArenaCustomLegend) => void
}

export interface ArenaCurrency {
  code: string
  factor: number
  number?: number
  places?: number
  postSymbol?: string
  preSymbol?: string
}

export interface ArenaLegendColour {
  bgColor: string
  borderColor: string
  isSelected?: boolean
}

export interface ArenaSeatAvailabilityLegend {
  absoluteSaving?: number
  discountCode?: string
  discountDesc?: string
  originalSeatprice?: number
  originalSurcharge?: number
  percentageSaving?: number
  price?: number
  priceBandCode?: string
  seatprice?: number
  surcharge?: number
  ticketTypeCode?: string
  ticketTypeDesc?: string
}

export interface ArenaCustomLegendChange {
  absoluteSaving?: number
  discountCode?: string
  originalSeatprice?: number
  originalSurcharge?: number
  percentageSaving?: number
  price?: number
  priceBandCode?: string
  seatprice?: number
  surcharge?: number
  ticketTypeCode?: string
}

export interface ArenaCustomLegend {
  currency: ArenaCurrency
  legendChanges: Array<ArenaCustomLegendChange>
}

interface ArenaSeatBlock {
  desc?: string
  price_band_code?: string
  ticket_type_code?: string
}

export interface ArenaSelectedSeat {
  avgRating?: number
  bgColor?: string
  borderColor?: string
  discountCode?: string
  discountId?: string
  finance?: {
    discountCode?: string
    priceBandCode?: string
    price?: number
    seatprice?: number
    surcharge?: number
    ticketTypeCode?: string
  }
  isRestrictedView?: boolean
  key?: string
  label?: string
  price?: number
  priceBandCode?: string
  restrictedViewText?: string
  seatBlock?: ArenaSeatBlock | null
  seatId: string
  ticketTypeCode?: string
  ticketTypeDesc?: string
  uuid?: string
}

interface ArenaSeatSelectionPayload {
  currency?: ArenaCurrency
  legend?: Array<ArenaSeatAvailabilityLegend>
  seats?: Array<ArenaSelectedSeat>
  selectedSeats?: Array<ArenaSelectedSeat>
}

interface ArenaTrolleyPayload {
  trolleyToken?: string
  trolley_token?: string
}

export interface ArenaSendMethod {
  send_code: string
  source_code?: string
  sourceCode?: string
}

interface ArenaSendMethodsPayload {
  sendMethods?: Array<ArenaSendMethod>
}

export type ArenaEventName =
  | 'init'
  | 'event-load-success'
  | 'event-load-fail'
  | 'seatmap-load-success'
  | 'seatmap-load-fail'
  | 'performance-load-success'
  | 'performance-load-fail'
  | 'availability-load-success'
  | 'availability-load-fail'
  | 'send-methods-load-success'
  | 'send-methods-load-fail'
  | 'seat-information-loaded'
  | 'trolley-load-success'
  | 'trolley-load-fail'
  | 'seat-selection'
  | 'zoom-in'
  | 'zoom-out'
  | 'reset-chart'

export type ArenaEventPayload = ArenaSeatSelectionPayload | ArenaTrolleyPayload | ArenaSendMethodsPayload | Record<string, unknown>

export interface ArenaSelectionResult {
  currency?: ArenaCurrency
  reservationExpiresAt: string
  reservationToken: string
  selectedSeats: Array<ArenaSelectedSeat>
  trolleyToken: string
  totalPrice: number
}

export interface ArenaTrolleyItem {
  costPrice?: number
  currency?: string
  priceBandCode: string
  quantity?: number
  salePrice?: number
  ticketTypeCode: string
}

export interface ArenaProviderBookingData extends Record<string, unknown> {
  arenaTrolleyToken: string
  selectedSeats: Array<ArenaSelectedSeat>
  trolleyItems: Array<ArenaTrolleyItem>
}

export interface ArenaEventStateUpdate {
  currency?: ArenaCurrency
  error?: ArenaErrorType
  failedEventName?: ArenaEventName
  legend?: Array<ArenaSeatAvailabilityLegend>
  seats?: Array<ArenaSelectedSeat>
  sendMethod?: ArenaSendMethod
  selectedSeats?: Array<ArenaSelectedSeat>
  status?: ArenaStatus
  trolleyToken?: string
  shouldClearTrolleyToken?: boolean
}

export const ARENA_MIN_ZOOM_SCALE = 1
export const ARENA_ZOOM_SCALE_EPSILON = 0.01
export const ARENA_ZOOM_OUT_DISABLED_OPACITY = '0.4'

interface ArenaD3ZoomElement extends Element {
  __zoom?: { k: number }
}

export function getArenaChartZoomScale(root: ParentNode): number | undefined {
  const chart = root.querySelector('.map-svg') as ArenaD3ZoomElement | null
  return chart?.__zoom?.k
}

export function isArenaAtMinZoom(scale: number | undefined) {
  if (scale === undefined) return false
  return scale <= ARENA_MIN_ZOOM_SCALE + ARENA_ZOOM_SCALE_EPSILON
}

export function getArenaZoomOutButton(root: ParentNode) {
  const navigation = root.querySelector('app-navigation')
  if (!navigation) return undefined

  return navigation.querySelector('button[aria-label="Zoom Out"]')
    ?? navigation.querySelectorAll('button')[2]
}

export function patchArenaZoomControls(root: ParentNode) {
  const navigation = root.querySelector('app-navigation')
  navigation?.querySelectorAll('button').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return

    button.style.display = 'grid'
    button.style.placeItems = 'center'
    button.style.width = '24px'
    button.style.height = '24px'
    button.style.lineHeight = '1'
    button.style.padding = '0'
    button.style.border = '1px solid var(--palette-neutral-surface-light-foreground)'
    button.style.borderRadius = '6px'
    button.style.backgroundColor = 'var(--palette-neutral-surface-static-white)'
    button.querySelectorAll('svg, acl-icon').forEach((icon) => {
      if (!(icon instanceof HTMLElement || icon instanceof SVGElement)) return

      icon.style.display = 'block'
      icon.style.margin = 'auto'
      icon.style.width = '14px'
      icon.style.height = '14px'
    })
  })

  const zoomOutButton = getArenaZoomOutButton(root)
  if (!zoomOutButton || !(zoomOutButton instanceof HTMLButtonElement)) return

  const zoomScale = getArenaChartZoomScale(root)
  if (zoomScale === undefined) return

  const isAtMinZoom = isArenaAtMinZoom(zoomScale)
  zoomOutButton.setAttribute('data-le-arena-zoom-out', 'true')

  if (isAtMinZoom) {
    zoomOutButton.disabled = true
    zoomOutButton.setAttribute('aria-disabled', 'true')
    zoomOutButton.style.opacity = ARENA_ZOOM_OUT_DISABLED_OPACITY
    zoomOutButton.style.cursor = 'default'
    return
  }

  zoomOutButton.disabled = false
  zoomOutButton.removeAttribute('aria-disabled')
  zoomOutButton.style.opacity = ''
  zoomOutButton.style.cursor = ''
}

export const defaultArenaOptions: ArenaConfigOptions = {
  controls: { position: 'top-right', border: 'all' },
  disableScrollToZoom: false,
  hasCustomLegend: true,
  hideBanner: true,
  hideCurrencySymbol: false,
  hideOfferMessages: true,
  legend: { position: 'none' },
  legendColours: [
    { bgColor: '#105920', borderColor: '#105920' },
    { bgColor: '#e9ae6c', borderColor: '#e9ae6c' },
    { bgColor: '#5da6ff', borderColor: '#5da6ff' },
    { bgColor: '#f48189', borderColor: '#f48189' },
    { bgColor: '#9fdead', borderColor: '#9fdead' },
  ],
  mapPaddingBottom: 0,
  mapPaddingRight: 0,
  minimap: { position: 'none' },
  showBasket: false,
  unhandledSeats: { position: 'none' },
  zoomDepthOnSeatClick: 0,
}

const BACKEND_AVAILABILITY_EXPLANATION_PATTERNS = [
  /\b(this )?seats? (is|are) unavailable\b/,
  /\bpermanently unavailable\b/,
  /\bno longer available\b/,
  /\b(this )?seats? (is|are) not available\b/,
  /\bnot available for (sale|booking|reservation)\b/,
  /\bnot for sale\b/,
  /\bcannot be (reserved|selected|booked)\b/,
  /\bheld by (the )?backend\b/,
  /\b(reserved|backend).{0,40}\bsubstitut(e[ds]?|ion)\b/,
  /\bsubstitut(e[ds]?|ion)\b.{0,40}\b(reserved|backend|applied)\b/,
]

export function isBackendAvailabilityExplanation(message: string | undefined) {
  const normalised = message?.trim().toLowerCase()
  if (!normalised) return false

  return BACKEND_AVAILABILITY_EXPLANATION_PATTERNS.some(pattern => pattern.test(normalised))
}

export function getArenaCustomerSeatHoverMessages(seats: Array<ArenaSelectedSeat>) {
  return new Map(seats
    .filter(seat => seat.restrictedViewText && !seat.isRestrictedView && !isBackendAvailabilityExplanation(seat.restrictedViewText))
    .map(seat => [seat.label ?? seat.seatId, seat.restrictedViewText!]))
}

export const arenaEvents: Array<ArenaEventName> = [
  'init',
  'event-load-success',
  'event-load-fail',
  'seatmap-load-success',
  'seatmap-load-fail',
  'performance-load-success',
  'performance-load-fail',
  'availability-load-success',
  'availability-load-fail',
  'send-methods-load-success',
  'send-methods-load-fail',
  'seat-information-loaded',
  'trolley-load-success',
  'trolley-load-fail',
  'seat-selection',
]

const arenaScriptPromises = new Map<string, Promise<void>>()

function hasSeatSelectionPayload(payload: ArenaEventPayload): payload is ArenaSeatSelectionPayload {
  return 'selectedSeats' in payload
}

function hasLegendPayload(payload: ArenaEventPayload): payload is ArenaSeatSelectionPayload {
  return 'legend' in payload
}

function hasSeatsPayload(payload: ArenaEventPayload): payload is ArenaSeatSelectionPayload {
  return 'seats' in payload
}

function hasTrolleyPayload(payload: ArenaEventPayload): payload is ArenaTrolleyPayload {
  return 'trolleyToken' in payload || 'trolley_token' in payload
}

function hasSendMethodsPayload(payload: ArenaEventPayload): payload is ArenaSendMethodsPayload {
  return 'sendMethods' in payload
}

export function loadArenaScript(scriptSrc: string) {
  const existingPromise = arenaScriptPromises.get(scriptSrc)
  if (existingPromise) return existingPromise

  const nextPromise = loadScript(scriptSrc, { async: true })
    .then(() => {
      arenaScriptPromises.delete(scriptSrc)
    })
    .catch((error) => {
      arenaScriptPromises.delete(scriptSrc)
      throw error
    })
  arenaScriptPromises.set(scriptSrc, nextPromise)
  return nextPromise
}

export function clearArenaScriptCache(scriptSrc?: string) {
  if (scriptSrc) {
    arenaScriptPromises.delete(scriptSrc)
    return
  }
  arenaScriptPromises.clear()
}

export function getTrolleyToken(payload: ArenaTrolleyPayload | ArenaReserveResult | string | undefined) {
  if (!payload) return undefined
  if (typeof payload === 'string') return payload
  return payload.trolleyToken ?? payload.trolley_token
}

export function getArenaReservationExpiresAt(payload: ArenaReserveResult | undefined): string | undefined {
  if (!payload) return undefined

  const reserveStartedAt = payload.reserve_iso8601_date_and_time
  const minutesLeftOnReserve = payload.minutes_left_on_reserve
  if (!reserveStartedAt || minutesLeftOnReserve === undefined || !Number.isFinite(minutesLeftOnReserve)) {
    return undefined
  }

  const reserveStartedAtMs = Date.parse(reserveStartedAt)
  if (!Number.isFinite(reserveStartedAtMs)) return undefined

  return new Date(reserveStartedAtMs + minutesLeftOnReserve * 60 * 1000).toISOString()
}

export interface ArenaReservationHold {
  reservationExpiresAt: string
  reservationToken: string
}

export function getArenaReservationHold(payload: ArenaReserveResult | string | undefined): ArenaReservationHold | undefined {
  if (!payload || typeof payload === 'string' || !hasValidArenaReserveResult(payload)) return undefined

  const reservationToken = payload.trolley_contents?.transaction_uuid
  const reservationExpiresAt = getArenaReservationExpiresAt(payload)
  if (!reservationToken || !reservationExpiresAt) return undefined

  return { reservationExpiresAt, reservationToken }
}

export function hasValidArenaReserveResult(payload: ArenaReserveResult | string | undefined) {
  if (!payload) return false
  if (typeof payload === 'string') return false
  return !!getTrolleyToken(payload) &&
    !!payload.trolley_contents?.transaction_uuid &&
    !!getArenaReservationExpiresAt(payload) &&
    !payload.input_contained_unavailable_order &&
    !payload.unreserved_orders?.length
}

export function getArenaReservationFailureReason(payload: ArenaReserveResult | undefined): ArenaReservationFailureReason {
  if (!payload) return 'reserve-failed'
  const failureComment = payload.unreserved_orders
    ?.map(order => order.reserve_failure_comment?.toLowerCase())
    .find(Boolean)

  if (failureComment?.includes('single')) return 'leaves-single-seat'
  if (payload.unreserved_orders?.some(order => order.seat_request_status === 'got_none')) return 'seat-unavailable'
  if (payload.unreserved_orders?.length) return 'some-seats-unavailable'
  if (payload.input_contained_unavailable_order) return 'seat-unavailable'
  if (!payload.trolley_contents?.transaction_uuid || !getArenaReservationExpiresAt(payload)) return 'missing-transaction'
  return 'reserve-failed'
}

export function getSeatPrice(seat: ArenaSelectedSeat, currency?: ArenaCurrency) {
  if (seat.price === undefined) return undefined
  const factor = currency?.factor ?? 1
  return factor > 1 ? seat.price / factor : seat.price
}

export function getTotalPrice(seats: Array<ArenaSelectedSeat>, currency?: ArenaCurrency) {
  return seats.reduce((total, seat) => total + (getSeatPrice(seat, currency) ?? 0), 0)
}

export function getArenaSeatTicketTypeCode(seat: ArenaSelectedSeat) {
  return seat.ticketTypeCode ?? seat.finance?.ticketTypeCode ?? seat.seatBlock?.ticket_type_code
}

export function getArenaSeatPriceBandCode(seat: ArenaSelectedSeat) {
  return seat.priceBandCode ?? seat.finance?.priceBandCode ?? seat.seatBlock?.price_band_code
}

export function getArenaSeatDiscountCode(seat: ArenaSelectedSeat) {
  return seat.discountCode ?? seat.discountId ?? seat.finance?.discountCode ?? 'NORMAL'
}

export function getArenaSendMethodSourceCode(sendMethod: ArenaSendMethod) {
  return sendMethod.source_code ?? sendMethod.sourceCode ?? 'email'
}

export interface ArenaReservableSeatGroup {
  discountCode: string
  priceBandCode: string
  seats: Array<ArenaSelectedSeat>
  ticketTypeCode: string
}

export function getArenaReservableSeatGroups(seats: Array<ArenaSelectedSeat>): Array<ArenaReservableSeatGroup> {
  const groups = new Map<string, ArenaReservableSeatGroup>()

  seats.forEach((seat) => {
    const ticketTypeCode = getArenaSeatTicketTypeCode(seat)
    const priceBandCode = getArenaSeatPriceBandCode(seat)
    if (!ticketTypeCode || !priceBandCode) return

    const discountCode = getArenaSeatDiscountCode(seat)
    const key = `${ticketTypeCode}:${priceBandCode}:${discountCode}`
    const group = groups.get(key) ?? {
      discountCode,
      priceBandCode,
      seats: [],
      ticketTypeCode,
    }

    group.seats.push(seat)
    groups.set(key, group)
  })

  return Array.from(groups.values())
}

export function hasMixedArenaReservableSeatGroups(seats: Array<ArenaSelectedSeat>) {
  return getArenaReservableSeatGroups(seats).length > 1
}

function getArenaProductIdCandidates(
  performanceId: string | undefined,
  ticketTypeCode: string,
  priceBandCode: string,
  discountCode?: string,
) {
  if (!performanceId) return []

  const priceBandCodes = new Set([
    priceBandCode,
    priceBandCode.includes('/') ? priceBandCode.split('/')[0] : `${priceBandCode}/pool`,
  ])

  return Array.from(priceBandCodes).flatMap((code) => {
    const productIds = [`${performanceId}:${ticketTypeCode}:${code}`]
    if (discountCode && discountCode !== 'NORMAL') productIds.push(`${performanceId}:${ticketTypeCode}:${code}~${discountCode}`)
    return productIds
  })
}

export function getArenaTicketCounts(
  selection: ArenaSelectionResult,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
): Record<string, number> {
  const ticketIds = selection.selectedSeats.map(seat => getArenaSeatTicket(seat, selectedTimeSlot)?.id)

  return countByAsRecord(nonNullable(ticketIds), ticketId => ticketId)
}

export function getArenaSeatTicket(
  seat: ArenaSelectedSeat,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
): App.ExperienceItemTicket | undefined {
  const ticketTypeCode = getArenaSeatTicketTypeCode(seat)
  const priceBandCode = getArenaSeatPriceBandCode(seat)
  if (!ticketTypeCode || !priceBandCode) return undefined
  const discountCode = getArenaSeatDiscountCode(seat)

  const ticketsByProductId = new Map(selectedTimeSlot.tickets.map(ticket => [ticket.productId, ticket]))
  const matchingProductId = getArenaProductIdCandidates(
    selectedTimeSlot.providerData?.performanceId,
    ticketTypeCode,
    priceBandCode,
    discountCode,
  ).find(productId => ticketsByProductId.has(productId))

  return matchingProductId ? ticketsByProductId.get(matchingProductId) : undefined
}

export function getArenaDisplaySeatPrice(
  seat: ArenaSelectedSeat,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
): number | undefined {
  return getArenaSeatTicket(seat, selectedTimeSlot)?.price
}

export function getArenaDisplayTotalPrice(
  seats: Array<ArenaSelectedSeat>,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
) {
  return seats.reduce((total, seat) => total + (getArenaDisplaySeatPrice(seat, selectedTimeSlot) ?? 0), 0)
}

export function getArenaDisplayCurrency(currencyCode: string, regionCode?: string): ArenaCurrency {
  const resolvedRegionCode = regionCode
    ?? config.regions.find(region => region.currencyCode === currencyCode)?.code
    ?? config.defaultRegion
  const { preSymbol, postSymbol } = getCurrencySymbolAffixes(currencyCode, resolvedRegionCode)

  return {
    code: currencyCode,
    factor: 1,
    places: 2,
    preSymbol,
    postSymbol,
  }
}

function getArenaLegendTicket(
  legendItem: ArenaSeatAvailabilityLegend,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
): App.ExperienceItemTicket | undefined {
  const { ticketTypeCode, priceBandCode, discountCode } = legendItem
  if (!ticketTypeCode || !priceBandCode) return undefined

  const ticketsByProductId = new Map(selectedTimeSlot.tickets.map(ticket => [ticket.productId, ticket]))
  const matchingProductId = getArenaProductIdCandidates(
    selectedTimeSlot.providerData?.performanceId,
    ticketTypeCode,
    priceBandCode,
    discountCode ?? 'NORMAL',
  ).find(productId => ticketsByProductId.has(productId))

  return matchingProductId ? ticketsByProductId.get(matchingProductId) : undefined
}

export function getArenaCustomLegend(
  legend: Array<ArenaSeatAvailabilityLegend>,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
  displayCurrencyCode: string,
): ArenaCustomLegend {
  return {
    currency: getArenaDisplayCurrency(displayCurrencyCode),
    legendChanges: legend.map((legendItem): ArenaCustomLegendChange => {
      const ticket = getArenaLegendTicket(legendItem, selectedTimeSlot)
      const displayPrice = ticket?.price ?? legendItem.price

      return {
        absoluteSaving: legendItem.absoluteSaving,
        discountCode: legendItem.discountCode ?? 'NORMAL',
        originalSeatprice: displayPrice,
        originalSurcharge: 0,
        percentageSaving: 0,
        price: displayPrice,
        priceBandCode: legendItem.priceBandCode,
        seatprice: displayPrice,
        surcharge: 0,
        ticketTypeCode: legendItem.ticketTypeCode,
      }
    }),
  }
}

export function areAllArenaSeatsMatchedToTickets(
  seats: Array<ArenaSelectedSeat>,
  selectedTimeSlot: App.ExperienceAvailabilityTimeSlot,
) {
  return seats.every(seat => !!getArenaSeatTicket(seat, selectedTimeSlot))
}

export function getArenaTrolleyItems(selection: ArenaSelectionResult): Array<ArenaTrolleyItem> {
  const trolleyItems = new Map<string, ArenaTrolleyItem>()

  selection.selectedSeats.forEach((seat) => {
    const ticketTypeCode = getArenaSeatTicketTypeCode(seat)
    const priceBandCode = getArenaSeatPriceBandCode(seat)
    if (!ticketTypeCode || !priceBandCode) return

    const key = `${ticketTypeCode}:${priceBandCode}`
    const existingTrolleyItem = trolleyItems.get(key)

    if (existingTrolleyItem) {
      trolleyItems.set(key, {
        ...existingTrolleyItem,
        quantity: (existingTrolleyItem.quantity ?? 1) + 1,
      })
      return
    }

    const salePrice = seat.finance?.price ?? seat.price

    trolleyItems.set(key, {
      ticketTypeCode,
      priceBandCode,
      salePrice,
      costPrice: salePrice,
      currency: selection.currency?.code,
    })
  })

  return Array.from(trolleyItems.values())
}

export function getArenaProviderBookingData(selection: ArenaSelectionResult): ArenaProviderBookingData {
  return {
    arenaTrolleyToken: selection.trolleyToken,
    selectedSeats: selection.selectedSeats,
    trolleyItems: getArenaTrolleyItems(selection),
  }
}

export function getArenaEventStateUpdate(eventName: ArenaEventName, payload: ArenaEventPayload): ArenaEventStateUpdate {
  if (eventName.endsWith('-fail')) {
    return {
      error: 'event-failed',
      failedEventName: eventName,
      status: 'failed',
    }
  }

  if (eventName === 'seat-selection' && hasSeatSelectionPayload(payload)) {
    return {
      currency: payload.currency,
      error: undefined,
      legend: payload.legend,
      selectedSeats: [...(payload.selectedSeats ?? [])],
      shouldClearTrolleyToken: true,
      status: 'ready',
    }
  }

  if (eventName === 'trolley-load-success' && hasTrolleyPayload(payload)) {
    return {
      error: undefined,
      status: 'reserved',
      trolleyToken: getTrolleyToken(payload),
    }
  }

  if (eventName === 'send-methods-load-success' && hasSendMethodsPayload(payload)) {
    return {
      error: undefined,
      sendMethod: payload.sendMethods?.[0],
    }
  }

  if (eventName === 'seat-information-loaded' && hasLegendPayload(payload)) {
    return {
      error: undefined,
      legend: payload.legend,
      ...(hasSeatsPayload(payload) ? { seats: payload.seats } : undefined),
    }
  }

  return { error: undefined }
}
